import type { CropSolution } from '../types';

/**
 * Crop → product recommendations, built ONLY from verified per-crop dosage/usage data in the
 * product sheets. No yield or income claims.
 *
 * `productSlugs` are resolved through `lib/slug.PRODUCT_SLUG_ALIASES`, so an editorial slug still
 * finds its product when the catalogue records a different name for it (C4L Biofoliar is listed in
 * the live catalogue as "Crop4Life BIOFOLIAR", and five crop pages were silently dropping it).
 * `aliases` are the names the DOSE TABLES use for this crop, so each page can show the real,
 * verbatim dose rows documented for it. `npm run check:seo` fails if either link breaks.
 */
export const cropSolutions: CropSolution[] = [
  {
    slug: 'grape',
    aliases: ['Grape', 'Grapes'],
    name: 'Grape',
    group: 'Fruits',
    intro:
      'A deep-rooted vineyard crop where soil biology, calcium and botanical strength support quality bunches and even ripening.',
    productSlugs: ['agn', 'biovin', 'nature-s-green-4-plus', 'c4l-biofoliar'],
    note: 'AGN is scheduled after April and October pruning and again at fruiting; Nature’s Green 4+ from first shoot and around flowering.',
  },
  {
    slug: 'pomegranate',
    aliases: ['Pomegranate'],
    name: 'Pomegranate',
    group: 'Fruits',
    intro: 'An orchard crop that benefits from strong soil biology through pruning, flowering and fruiting.',
    productSlugs: ['agn', 'nature-s-green-4-plus', 'c4l-biofoliar', 'fulvic-25'],
    note: 'AGN after pruning (first irrigation), at flowering and at fruiting; foliar strength at blossom and fruit set.',
  },
  {
    slug: 'sugarcane',
    aliases: ['Sugarcane'],
    name: 'Sugarcane',
    group: 'Field Crops',
    intro: 'A long-duration field crop where root-zone biology and biostimulants support growth across the grand-growth phases.',
    productSlugs: ['agn', 'fulvic-25', 'aminopf', 'biodynamic'],
    note: 'AGN is applied up to four times — from 15–25 days after planting through the two grand-growth phases.',
  },
  {
    slug: 'citrus',
    aliases: ['Citrus', 'Orange', 'Lemon', 'Sweet lemon'],
    name: 'Citrus',
    group: 'Fruits',
    intro: 'Orange, lemon and sweet lemon respond to soil-biology and foliar strength through irrigation, flowering and fruiting.',
    productSlugs: ['agn', 'c4l-biofoliar', 'nature-s-green-4-plus', 'fulvic-25'],
    note: 'AGN at first irrigation, flowering and fruiting; foliar inputs support quality and stress tolerance.',
  },
  {
    slug: 'cotton',
    aliases: ['Cotton'],
    name: 'Cotton',
    group: 'Field Crops',
    intro: 'A field crop where early soil biology and amino-acid nutrition support establishment through squaring.',
    productSlugs: ['agn', 'aminopf', 'fulvic-25', 'plant-armor-elite'],
    note: 'AGN at the 4–5 leaf stage and again from emergence to first square.',
  },
  {
    slug: 'banana',
    aliases: ['Banana'],
    name: 'Banana',
    group: 'Fruits',
    intro: 'A high-demand crop supported by soil biology and calcium foliar nutrition from the juvenile phase to bunch development.',
    productSlugs: ['agn', 'nature-s-green-4-plus', 'aminopf', 'fulvic-25'],
    note: 'AGN through juvenile, flower-bud and bunch stages; Nature’s Green 4+ from foliage to ripening.',
  },
  {
    slug: 'tomato',
    aliases: ['Tomato'],
    name: 'Tomato',
    group: 'Vegetables',
    intro: 'A shallow-rooted vegetable that responds to the AGN LTE programme plus amino-acid and calcium foliar support.',
    productSlugs: ['agn-lte', 'aminopf', 'nature-s-green-4-plus', 'c4l-biofoliar'],
    note: 'AGN LTE at 15–20 and 45–50 days after transplanting; foliar inputs every 10–14 days.',
  },
  {
    slug: 'chilli',
    aliases: ['Chilli'],
    name: 'Chilli',
    group: 'Vegetables',
    intro: 'A crop with well-documented foliar programmes for amino acids, fulvic and botanical strength.',
    productSlugs: ['agn-lte', 'c4l-biofoliar', 'fulvic-25', 'aminopf'],
    note: 'C4L Biofoliar three foliar applications; Fulvic 25 and AminOPF two foliar applications each.',
  },
  {
    slug: 'potato',
    aliases: ['Potato'],
    name: 'Potato',
    group: 'Vegetables',
    intro: 'A tuber crop supported by root-zone biology and calcium foliar nutrition from the 4–6 leaf stage.',
    productSlugs: ['agn-lte', 'nature-s-green-4-plus', 'biodynamic', 'fulvic-25'],
    note: 'AGN LTE at the 4–5 leaf stage and 45–50 days; Nature’s Green 4+ every three weeks from the 4th–6th leaf.',
  },
  {
    slug: 'onion',
    aliases: ['Onion'],
    name: 'Onion',
    group: 'Vegetables',
    intro: 'A shallow-rooted bulb crop that benefits from soil biology and balanced nutrition after transplanting.',
    productSlugs: ['agn-lte', 'biodynamic', 'aminopf', 'nature-s-green-4-plus'],
    note: 'AGN LTE at 15–20 and 45–50 days after transplanting.',
  },
  {
    slug: 'wheat-paddy',
    aliases: ['Wheat', 'Paddy', 'Rice'],
    name: 'Wheat & Paddy',
    group: 'Field Crops',
    intro: 'Staple cereals where the AGN LTE programme and foliar calcium support early establishment and grain quality.',
    productSlugs: ['agn-lte', 'nature-s-green-4-plus', 'biodynamic', 'fulvic-25'],
    note: 'AGN LTE 15–20 days after planting; Nature’s Green 4+ from the 3rd–6th leaf through vegetative growth.',
  },
  {
    slug: 'turmeric-ginger',
    aliases: ['Turmeric', 'Ginger'],
    name: 'Turmeric & Ginger',
    group: 'Plantation & Spices',
    intro: 'Rhizome spice crops with a staged soil-biology programme across the long growing season.',
    productSlugs: ['agn', 'biodynamic', 'fulvic-25', 'aminopf'],
    note: 'AGN at 30–45, 90–100 and 150–160 days after planting.',
  },
]
